import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, Subscription, of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import { AuthService } from '../../services/auth.service';
import * as fromApp from '../../store/app.reducers';
import {
    getCartSamplingSupplyItems,
    getCartServiceItems,
    getIsContactChecked,
} from '../../store/app.reducers';
import {
    getBillingDetails,
    getDeliveryState,
    getOrderReferences,
    getSameInvoiceAddressSwitch,
    getUserDetails,
    getValidationStatus
} from '../store';
import { OrderReferences, UserDetails } from '../shared/model';
import { State as DeliveryDetailsState } from '../store/delivery-details/delivery-details.reducers';
import { State as ContactState } from '../../store/labsys-contact/labsys-contact.reducers';
import { Address } from '../../model/address.model';
import { map, mergeMap, skipWhile, takeUntil, withLatestFrom } from 'rxjs/operators';
import { CartItem } from '../../model/cart.model';
import { Price } from '../../model/catalogue-category/price.model';
import {
    AddOrderInformation,
    SetError,
    ValidateFail,
    ValidationStatus,
} from '../store/order-checkout/order-checkout.actions';
import { CheckoutValidationService } from '../services/checkout-validation.service';
import * as CartActions from '../../store/cart/cart.actions';
import { getTransformPhoneAndMobile } from '../../shared/utils/form-controls';
import { LimbusErrorResponse } from '../../model/limbus-error-response.model';
import { cartHasServiceItemsOnly } from '../shared/cart';
import { environment } from '../../../environments/environment';
import { remove } from '../../utils/util-functions';
import { isValidAddress } from '../shared/util';
import { OrderInformation } from '@app/model/order-information.model';

@Injectable()
export class CheckoutGuard  implements OnDestroy {
    promoCode: any;
    private userDetailsSub$: Subscription;
    private orderReferencesSub$: Subscription;
    private deliverySub$: Subscription;
    private invoiceSub$: Subscription;
    private contactSub$: Subscription;
    private supplyItemsSub$: Subscription;
    private serviceItemsSub$: Subscription;
    private onDestroy$ = new Subject<void>();
    private skipValidation: boolean;

    supplyItems: CartItem[];
    serviceItems: CartItem[];
    userDetailsFromState: UserDetails;
    orderReferencesFromState: OrderReferences;
    deliveryFromState: DeliveryDetailsState;
    invoiceFromState: Address;
    isInvoiceSameAsDelivery: boolean;
    pageToValidateCounts: { [key: string]: number } = {};

    invoiceAddressSwitchSub$: Subscription;

    constructor(
        private auth: AuthService,
        public store: Store<fromApp.AppState>,
        public router: Router,
        public validator: CheckoutValidationService) {

        this.promoCode = environment['promoCode'];

        this.userDetailsSub$ = this.store.select(getUserDetails)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(userDetails => {
                this.userDetailsFromState = cloneDeep(userDetails);
            });
        this.orderReferencesSub$ = this.store.select(getOrderReferences)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => {
                this.orderReferencesFromState = cloneDeep(data);
            });
        this.deliverySub$ = this.store.select(getDeliveryState)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => {
                this.deliveryFromState = cloneDeep(data);
            });
        this.invoiceSub$ = this.store.select(getBillingDetails)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => {
                this.invoiceFromState = cloneDeep(data);
            });
        this.invoiceAddressSwitchSub$ = this.store.select(getSameInvoiceAddressSwitch)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => {
                this.isInvoiceSameAsDelivery = data;
            });
        this.contactSub$ = this.store.select('contact')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((contacts) => {
                const contact = !!contacts.loggedInContact ? contacts.loggedInContact : null;

                this.skipValidation = !!contact;

            });
        this.supplyItemsSub$ = this.store.select(getCartSamplingSupplyItems)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => {
                this.supplyItems = data;
            });
        this.serviceItemsSub$ = this.store.select(getCartServiceItems)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => {
                this.serviceItems = data;
            });

    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

        return this.store.select(getIsContactChecked)
            .pipe(
                skipWhile(isContactChecked => !isContactChecked),
                withLatestFrom(
                    this.store.select(fromApp.getCartServiceItems),
                    this.store.select(fromApp.getCartPrice),
                    this.store.select(getValidationStatus),
                    this.store.select('contact')
                ),
                mergeMap(handleSub(this)),
                map(a => a)
            );
    }

}

type handleSubParamType = [
    boolean,
    CartItem[],
    Price,
    ValidationStatus,
    ContactState
];

const handleSub = (guard: CheckoutGuard) => (
    [isContactChecked, items, price, status, contacts]: handleSubParamType): Observable<boolean> => {

    const contact = getContactFromContacts(contacts);
    // CP-1207: Skip checkout validation for the cart rewrite since Limbus COS will handle order validation
    const skipValidation = true;

    const payloadForValidation = getOrderInfoForValidation(
        guard.userDetailsFromState,
        guard.orderReferencesFromState,
        guard.deliveryFromState,
        guard.invoiceFromState,
        guard.isInvoiceSameAsDelivery,
        guard.supplyItems,
        guard.serviceItems,
        contacts,
        price,
        guard.promoCode
    );

    const payload = getOrderInfo(
        false,
        guard.userDetailsFromState,
        guard.orderReferencesFromState,
        guard.deliveryFromState,
        guard.invoiceFromState,
        guard.isInvoiceSameAsDelivery,
        guard.supplyItems,
        guard.serviceItems,
        contacts,
        price,
        guard.promoCode
    );
    if (skipValidation) {
        guard.store.dispatch(new AddOrderInformation(payload));
        return of(true);
    }

    guard.store.dispatch(new AddOrderInformation(
        payload
    ));

    const result: Observable<boolean> = guard.validator.validate(payloadForValidation)
        .pipe(
            map((response: Observable<any | any[]>) => {
                if (Array.isArray(response)) {
                    const [errorAction, validateAction] = response;

                    if (validateAction instanceof ValidateFail
              || validateAction instanceof CartActions.PlaceOrderFailure) {

                        const pageToValidate = getPageToComplete(
                            errorAction.errors,
                            cartHasServiceItemsOnly(guard.supplyItems, guard.serviceItems)
                        );
                        guard.store.dispatch(validateAction);
                        guard.router.navigate([`/checkout/steps/${pageToValidate}`]);

                        // show error if the page to validate was already visited for validation
                        const count = guard.pageToValidateCounts[pageToValidate];
                        const visited = !!count && count > 0;
                        if (visited) {
                            guard.store.dispatch(new SetError(['Please complete your details.']));
                        }
                        guard.pageToValidateCounts[pageToValidate] = !!count ? count + 1 : 1;
                        return false;
                    }
                }
                guard.store.dispatch(new AddOrderInformation(payload));
                return true;
            })
        );

    return result;
};

const createError = () => {
    const error = {
        'status': {
            'resourceType': 'error-status.v1',
            'value': 400,
            'name': 'BAD_REQUEST'
        },
        'messages': [{
            'resourceType': 'reference-with-message.v1',
            'reference': '/',
            'message': 'Please complete your details.'
        }]
    };

    return new LimbusErrorResponse(
        error.status,
        error.messages,
        []
    );

};

export const getPageToComplete = (error: LimbusErrorResponse, hasServiceItemsOnly: boolean) => {

    const pageMap = {
        'user-details': ['firstName', 'lastName', 'email', 'phone', 'mobile'],
        'delivery': ['deliveryAddress', 'comments'],
        'billing': ['invoiceAddress']
    };

    const testFn = (value, mapkey) => !!value.match(pageMap[mapkey].join('|'));

    const longMsgs = error.messages
        .map(msg => `${msg.reference} ${msg.message}`);

    let key = 'user-details';
    let test = longMsgs.some(msg => testFn(msg, key));
    if (!!test) {
        return key;
    }

    key = 'delivery';
    test = longMsgs.some(msg => testFn(msg, key));
    if (!!test && !hasServiceItemsOnly) {
    // the app will hide delivery form if there are only service items and will use invoice address as delivery address
    // if delivery address is invalid just return the invoice/billing page since delivery page === invoice address
        return key;
    }

    key = 'billing';
    test = longMsgs.some(msg => testFn(msg, key));
    if (!!test) {
        return key;
    }

    return 'review-order';

};

export const isUserDetailsCompleted = (payload) => {
    const { firstName, lastName, email, phone, mobile } = payload;
    return !!firstName
    && !!lastName
    && email
    && (!!phone || !!mobile);
};

export const getOrderInfoForValidation = (
    userDetailsFromState,
    orderReferencesFromState,
    deliveryFromState,
    invoiceFromState,
    isInvoiceSameAsDelivery,
    supplyItems,
    serviceItems,
    contacts,
    price,
    promoCode = null) => getOrderInfo(
    true,
    userDetailsFromState,
    orderReferencesFromState,
    deliveryFromState,
    invoiceFromState,
    isInvoiceSameAsDelivery,
    supplyItems,
    serviceItems,
    contacts,
    price,
    promoCode
);

export const getOrderInfo = (
    forValidation,
    userDetailsFromState,
    orderReferencesFromState,
    deliveryFromState,
    invoiceFromState,
    isInvoiceSameAsDelivery,
    supplyItems,
    serviceItems,
    contacts,
    price,
    promoCode = null
): OrderInformation => {

    let payload = new OrderInformation();

    if (!userDetailsFromState) {
        payload = getTransformPhoneAndMobile(new UserDetails());
    } else {
        payload = getTransformPhoneAndMobile(userDetailsFromState);
    }

    const contact = getContactFromContacts(contacts);

    if (!!contacts.account) {
        const organisationId = contacts.account.id;
        const company = contacts.account.name;
        payload = { ...payload, organisationId, company };
    }

    // business logic change 23 may 2019: always validate
    const skipValidation = false && !!contact;

    // exclude other details if one of the following is empty firstname, lastname, email, phone, mobile
    // to display validation error for phone or mobile
    if (skipValidation || isUserDetailsCompleted(payload)) {
        if (!!orderReferencesFromState) {
            payload = { ...payload, ...orderReferencesFromState };
        }

        if (!!promoCode) {
            payload = { ...payload, customerOtherReference : promoCode };
        }
        if (!deliveryFromState || !deliveryFromState.address) {
            payload = { ...payload, deliveryAddress: new Address() };
        } else {
            const { address: deliveryAddress, comments } = deliveryFromState;
            payload = { ...payload, deliveryAddress, comments };
        }

        if (isInvoiceSameAsDelivery && !contact) {
            const invoiceAddress = cloneDeep(payload['deliveryAddress']);
            payload = { ...payload, invoiceAddress };
        } else if (!invoiceFromState) {
            const invoiceAddress = new Address();
            payload = { ...payload, invoiceAddress };
        } else {
            payload = { ...payload, invoiceAddress: invoiceFromState };
        }

        const hasSupplyItem = supplyItems.length > 0;
        const hasServiceItem = serviceItems.length > 0;

        const isDeliveryAddressValid  = isValidAddress(payload['deliveryAddress']);
        const isInvoiceAddressValid = isValidAddress(payload['invoiceAddress']);

        // business logic: use deliveryAddress when invoiceAddress is empty and vice versa
        if (!isDeliveryAddressValid && isInvoiceAddressValid) {
            payload = { ...payload, deliveryAddress: cloneDeep(payload['invoiceAddress']) };
        } else if (!isInvoiceAddressValid && isDeliveryAddressValid) {
            payload = { ...payload, invoiceAddress: cloneDeep(payload['deliveryAddress']) };
        }

        // use invoice address as delivery address if there's no supply items and there is(are) test(s)
        // and only if delivery address is not valid
        if (!hasSupplyItem && hasServiceItem) {
            // treat deliveryAddress line 1 special because it is required
            if (!isDeliveryAddressValid) {
                payload = { ...payload, deliveryAddress: cloneDeep(payload['invoiceAddress']) };
            }
        }

        // if there (is/are) only service items, don't validate delivery address
        if (forValidation && !hasSupplyItem) {
            // delete payload['deliveryAddress'];
            payload = remove(payload, 'deliveryAddress');
        }

        // if amount is zero, don't validate billing address
        if (price && price.amount <= 0 && !hasServiceItem) {
            // delete payload['invoiceAddress'];
            payload = remove(payload, 'invoiceAddress');
        }

    }
    if (supplyItems.length > 0) {
        payload.deliveryAddress = deliveryFromState.address ?? payload.deliveryAddress;
        payload.comments = deliveryFromState.comments  ?? payload.comments;
    }
    if (price && price.amount > 0) {
        payload.invoiceAddress = invoiceFromState ?? (payload.invoiceAddress ??
            isInvoiceSameAsDelivery ? payload.deliveryAddress : null);
    }

    return payload;
};

export const getContactFromContacts = (contacts) => !!contacts.loggedInContact ? contacts.loggedInContact : null;



